import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { canView } from "@/lib/permissions";
import { api } from "@/lib/api";
import { acquisitionsApi, type DashboardStats as AcqDashboardStats } from "@/lib/api/acquisitions";
import {
  ArrowLeftRight,
  Book,
  Clock,
  AlertCircle,
  Users,
  Library,
  FileText,
  Search,
  CreditCard,
  UserCheck,
  Package,
  ClipboardList,
  Building2,
  MapPin,
  Bell,
  User,
  HelpCircle,
} from "lucide-react";

interface DashboardStats {
  catalog?: { totalItems: number };
  patrons?: { active: number };
  circulation?: {
    checkoutsToday: number;
    returnsToday: number;
    overdueItems: number;
    activeCheckouts: number;
    pendingReservations: number;
  };
}

export function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const permissions = user?.permissions || [];
  const staffName = user?.firstName || "Staff";
  const roleName = user?.roleName || "Staff Member";
  const branchName = user?.branchName;
  const departmentName = user?.departmentName;

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [acqStats, setAcqStats] = useState<AcqDashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const hasPermission = (perm: string) => canView(perm, permissions);

  const showCirculation =
    hasPermission("circulation.checkOut") ||
    hasPermission("circulation.checkIn") ||
    hasPermission("circulation.borrow") ||
    hasPermission("circulation.return") ||
    hasPermission("circulation.renew");
  const showCatalog =
    hasPermission("catalog.view") ||
    hasPermission("catalog.create") ||
    hasPermission("catalog.list");
  const showPatrons = hasPermission("patron.view") || hasPermission("patron.list");
  const showFines = hasPermission("fines.view") || hasPermission("fines.collect");
  const showReports =
    hasPermission("reports.circulation") || hasPermission("reports.viewPatronLogs");
  const showRegistration =
    hasPermission("registration.view") || hasPermission("registration.approve");
  const showCards =
    hasPermission("cards.view") ||
    hasPermission("cards.process") ||
    hasPermission("cards.markPickup");
  const showAcquisitions =
    hasPermission("acquisitions.view") || hasPermission("acquisitions.createRequest");
  const showNotifications =
    hasPermission("notifications.view") || hasPermission("notifications.send");
  const showStats = hasPermission("dashboard.viewStats");

  const hasAnyStatContent = showCirculation || showCatalog || showPatrons || showAcquisitions;
  const hasAnyPending = showCirculation || showRegistration;
  const hasAnyQuickAction =
    showCirculation || showCatalog || showPatrons || showCards || showFines || showReports;

  useEffect(() => {
    if (!showStats) {
      setStatsLoading(false);
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      const tryLoad = async (): Promise<boolean> => {
        try {
          const res = await api.get("/dashboard/stats", { signal: controller.signal });
          if (!controller.signal.aborted) setStats(res.data);
          return true;
        } catch {
          return false;
        }
      };
      let ok = await tryLoad();
      if (!ok && !controller.signal.aborted) {
        await new Promise((r) => setTimeout(r, 400));
        if (!controller.signal.aborted) await tryLoad();
      }
      if (showAcquisitions && !controller.signal.aborted) {
        try {
          const acq = await acquisitionsApi.dashboard.stats();
          if (!controller.signal.aborted) setAcqStats(acq);
        } catch {
          // non-critical
        }
      }
      if (!controller.signal.aborted) setStatsLoading(false);
    };
    load();
    return () => controller.abort();
  }, [showStats, showAcquisitions]);

  if (showStats && statsLoading) {
    return <StaffSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Welcome back, {staffName}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
              {roleName}
            </span>
            {branchName && (
              <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                <MapPin className="w-3.5 h-3.5" />
                {branchName}
              </span>
            )}
            {departmentName && (
              <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                <Building2 className="w-3.5 h-3.5" />
                {departmentName}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {showStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {showCirculation && (
            <>
              <StatCard
                label="Checkouts Today"
                value={stats?.circulation?.checkoutsToday ?? 0}
                icon={<ArrowLeftRight className="w-5 h-5" />}
                iconBg="bg-primary/5 text-primary"
              />
              <StatCard
                label="Returns Today"
                value={stats?.circulation?.returnsToday ?? 0}
                icon={<Book className="w-5 h-5" />}
                iconBg="bg-primary/5 text-primary"
              />
              <StatCard
                label="Pending Renewals"
                value={stats?.circulation?.pendingReservations ?? 0}
                icon={<Clock className="w-5 h-5" />}
                iconBg="bg-amber-50 text-amber-600"
              />
              <StatCard
                label="Overdue Items"
                value={stats?.circulation?.overdueItems ?? 0}
                icon={<AlertCircle className="w-5 h-5" />}
                iconBg="bg-red-50 text-red-600"
                valueColor={
                  (stats?.circulation?.overdueItems ?? 0) > 0 ? "text-red-600" : undefined
                }
              />
            </>
          )}
          {showCatalog && (
            <StatCard
              label="Catalog Items"
              value={stats?.catalog?.totalItems ?? 0}
              icon={<Library className="w-5 h-5" />}
              iconBg="bg-primary/5 text-primary"
            />
          )}
          {showPatrons && (
            <StatCard
              label="Active Patrons"
              value={stats?.patrons?.active ?? 0}
              icon={<Users className="w-5 h-5" />}
              iconBg="bg-primary/5 text-primary"
            />
          )}
          {showAcquisitions && (
            <>
              <StatCard
                label="Open Requests"
                value={acqStats?.openRequests ?? 0}
                icon={<ClipboardList className="w-5 h-5" />}
                iconBg="bg-primary/5 text-primary"
              />
              <StatCard
                label="Pending Orders"
                value={acqStats?.pendingOrders ?? 0}
                icon={<Package className="w-5 h-5" />}
                iconBg="bg-amber-50 text-amber-600"
              />
            </>
          )}
          {!hasAnyStatContent && (
            <div className="col-span-full text-center py-6">
              <Library className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium text-slate-900">No statistics available</p>
              <p className="text-xs text-slate-500 mt-1">
                Stats will appear here based on your permissions
              </p>
            </div>
          )}
        </div>
      )}

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Actions — always visible */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              Pending Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {showCirculation && (
                <>
                  <PendingItem
                    icon={<Clock className="w-4 h-4 text-amber-500" />}
                    title="Renewal Requests"
                    subtitle="Pending review"
                    variant="warning"
                    count="--"
                  />
                  <PendingItem
                    icon={<AlertCircle className="w-4 h-4 text-red-500" />}
                    title="Overdue Notices"
                    subtitle="Need to be sent"
                    variant="danger"
                    count="--"
                  />
                </>
              )}
              {showRegistration && (
                <PendingItem
                  icon={<UserCheck className="w-4 h-4 text-primary" />}
                  title="Registration Approvals"
                  subtitle="Awaiting review"
                  variant="info"
                  count="--"
                  onClick={() => navigate("/admin/registrations")}
                />
              )}
              {!hasAnyPending && (
                <div className="text-center py-6">
                  <UserCheck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium text-slate-900">All clear</p>
                  <p className="text-xs text-slate-500 mt-1">No pending actions for your role</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions — always visible */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {(hasPermission("circulation.checkOut") || hasPermission("circulation.borrow")) && (
                <QuickAction
                  icon={<ArrowLeftRight className="w-4 h-4" />}
                  label="Check Out Item"
                  description="Process a new checkout"
                  onClick={() => navigate("/circulation")}
                  highlighted
                />
              )}
              {(hasPermission("circulation.checkIn") || hasPermission("circulation.return")) && (
                <QuickAction
                  icon={<Book className="w-4 h-4" />}
                  label="Check In Item"
                  description="Process a return"
                  onClick={() => navigate("/circulation")}
                />
              )}
              {hasPermission("catalog.view") && (
                <QuickAction
                  icon={<Search className="w-4 h-4" />}
                  label="Search Catalog"
                  description="Find items in the library"
                  onClick={() => navigate("/catalog")}
                  highlighted={!showCirculation}
                />
              )}
              {hasPermission("catalog.create") && (
                <QuickAction
                  icon={<Library className="w-4 h-4" />}
                  label="Add New Item"
                  description="Catalog a new resource"
                  onClick={() => navigate("/catalog/isbn")}
                />
              )}
              {showPatrons && (
                <QuickAction
                  icon={<Users className="w-4 h-4" />}
                  label="Manage Patrons"
                  description="View and manage patron accounts"
                  onClick={() => navigate("/patrons")}
                />
              )}
              {showCards && (
                <QuickAction
                  icon={<CreditCard className="w-4 h-4" />}
                  label="Process Library Card"
                  description="Issue or renew a card"
                  onClick={() => navigate("/admin/cards")}
                />
              )}
              {showFines && (
                <QuickAction
                  icon={<CreditCard className="w-4 h-4" />}
                  label="Collect Fine"
                  description="Process a fine payment"
                  onClick={() => navigate("/fines")}
                />
              )}
              {showReports && (
                <QuickAction
                  icon={<FileText className="w-4 h-4" />}
                  label="View Reports"
                  description="Access reports and statistics"
                  onClick={() => navigate("/admin/reports/cataloging")}
                />
              )}
              {showAcquisitions && (
                <QuickAction
                  icon={<Package className="w-4 h-4" />}
                  label="Acquisitions insights"
                  description="Utilization, vendor spend, audit log"
                  onClick={() => navigate("/acquisitions/reports")}
                />
              )}

              {/* Always-available actions */}
              <QuickAction
                icon={<User className="w-4 h-4" />}
                label="My Profile"
                description="View and update your profile"
                onClick={() => navigate("/profile")}
              />
              <QuickAction
                icon={<HelpCircle className="w-4 h-4" />}
                label="Documentation"
                description="Guides and documentation"
                onClick={() => navigate("/documentation")}
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications panel */}
        {showNotifications && (
          <Card className="border border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base font-semibold text-slate-900">
                Recent Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm font-medium text-slate-900">No recent notifications</p>
                <p className="text-xs text-slate-500 mt-1">
                  Notifications will appear here when available
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Low-permission informational card */}
        {!hasAnyQuickAction && (
          <Card className="border border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-lg bg-primary/5 flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <p className="text-sm font-medium text-slate-900">
                  Welcome to {branchName || "the Library"}
                </p>
                <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
                  Your dashboard will show relevant tools and data as your administrator assigns
                  permissions to your role.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
  iconBg,
  valueColor,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  iconBg: string;
  valueColor?: string;
}) {
  return (
    <Card className="border border-slate-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${valueColor || "text-slate-900"}`}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

function PendingItem({
  icon,
  title,
  subtitle,
  variant,
  count,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  variant: "danger" | "warning" | "info";
  count: string | number;
  onClick?: () => void;
}) {
  const colors = {
    danger: "bg-red-50 border-red-100",
    warning: "bg-amber-50 border-amber-100",
    info: "bg-primary/5 border-primary/10",
  };
  const badgeColors = {
    danger: "bg-red-100 text-red-800",
    warning: "bg-amber-100 text-amber-800",
    info: "bg-primary/10 text-primary",
  };

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border ${colors[variant]} ${onClick ? "cursor-pointer hover:brightness-95 transition" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <p className="text-sm font-medium text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${badgeColors[variant]}`}>
        {count}
      </span>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  description,
  onClick,
  highlighted,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left cursor-pointer group ${
        highlighted ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-slate-50"
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          highlighted
            ? "bg-primary/10 text-primary"
            : "bg-slate-100 text-slate-600 group-hover:bg-primary/5 group-hover:text-primary"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${highlighted ? "text-primary" : "text-slate-900"}`}>
          {label}
        </p>
        <p className={`text-xs ${highlighted ? "text-primary" : "text-slate-500"}`}>
          {description}
        </p>
      </div>
    </button>
  );
}

function StaffSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading dashboard">
      <div>
        <div className="h-7 w-56 bg-slate-100 rounded animate-pulse" />
        <div className="flex items-center gap-2 mt-2">
          <div className="h-5 w-20 bg-primary/5 rounded-full animate-pulse" />
          <div className="h-4 w-24 bg-slate-50 rounded animate-pulse" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
                <div className="h-7 w-10 bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="w-10 h-10 rounded-lg bg-primary/5 animate-pulse" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="h-4 w-32 bg-slate-100 rounded animate-pulse mb-4" />
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="h-4 w-28 bg-slate-100 rounded animate-pulse mb-3" />
          <div className="space-y-1.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <div className="w-9 h-9 rounded-lg bg-slate-50 animate-pulse flex-shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-3.5 w-24 bg-slate-50 rounded animate-pulse" />
                  <div className="h-2.5 w-36 bg-slate-50/80 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <span className="sr-only">Loading dashboard statistics...</span>
    </div>
  );
}
